import { type PartnerStatus } from '@/api/routes/partners/partners.api';
import {
  type PartnerEvent,
  type PartnerEventAddress,
  type PartnerEventBody,
  type PartnerEventDetail,
  type PartnerEventReview,
  type PartnerEventsListParams,
} from '@/api/routes/partners/partners.events.api';
import { type ChipProps } from '@/components/Chip';
import {
  type EventFormSaveMode,
  type EventFormValues,
  type EventSubmitRequirement,
  type EventsTab,
  type PartnerEventActions,
  type PartnerEventDisplayStatus,
} from '@/domain/events/types';
import { getActiveLocale } from '@/i18n/activeLocale';
import { translateFile, translatedMap } from '@/i18n';

export const EVENTS_PAGE_SIZE = 20;

export const EVENTS_TABS: EventsTab[] = [
  'all',
  'draft',
  'in_review',
  'approved',
  'rejected',
  'canceled',
];

export const eventsTabLabel = translatedMap('domain.events.utils', {
  all: 'tabs.all',
  draft: 'tabs.draft',
  in_review: 'tabs.in_review',
  approved: 'tabs.approved',
  rejected: 'tabs.rejected',
  canceled: 'tabs.canceled',
} as const);

export const partnerEventStatusLabel = translatedMap('domain.events.utils', {
  draft: 'status.draft',
  in_review: 'status.in_review',
  approved: 'status.approved',
  rejected: 'status.rejected',
  canceled: 'status.canceled',
} as const);

export const PARTNER_EVENT_STATUS_CHIP: Record<
  PartnerEventDisplayStatus,
  Pick<ChipProps, 'variant' | 'color'>
> = {
  draft: { variant: 'outline', color: 'neutral' },
  in_review: { variant: 'solid', color: 'warning' },
  approved: { variant: 'solid', color: 'success' },
  rejected: { variant: 'solid', color: 'error' },
  canceled: { variant: 'outline', color: 'neutral' },
};

export const getPartnerEventDisplayStatus = (
  event: Pick<PartnerEvent, 'reviewStatus' | 'canceledAt'>,
): PartnerEventDisplayStatus => (event.canceledAt ? 'canceled' : event.reviewStatus);

/** The tab in the URL (`?status=`), falling back to every event for anything unknown. */
export const parseEventsTab = (value: string | null): EventsTab =>
  EVENTS_TABS.find((tab) => tab === value) ?? 'all';

export const toEventsListParams = (tab: EventsTab, page: number): PartnerEventsListParams => ({
  ...(tab === 'all' ? {} : { status: tab }),
  offset: page * EVENTS_PAGE_SIZE,
  limit: EVENTS_PAGE_SIZE,
});

/** A civil date (`YYYY-MM-DD…`) built in local time, so the day never shifts with the timezone. */
const toCivilDate = (date: string) => {
  const [year, month, day] = date.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
};

// 2024-09-01 was a Sunday: weekday N is that date plus N days (`weekdayShortLabel`).
const shortWeekday = (date: Date) => {
  const label = new Intl.DateTimeFormat(getActiveLocale(), { weekday: 'short' })
    .format(date)
    .replace('.', '');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/** "Qui, 16 out". */
export function formatEventShortDate(date: string): string {
  const civil = toCivilDate(date);
  const dayMonth = new Intl.DateTimeFormat(getActiveLocale(), { day: 'numeric', month: 'short' })
    .format(civil)
    .replace('.', '')
    .replace(' de ', ' ');
  return `${shortWeekday(civil)}, ${dayMonth}`;
}

/** "Qui, 16 out · 06:00", or the date alone when the event has no start time. */
export function formatEventWhen(date: string, startTime?: string | null): string {
  const day = formatEventShortDate(date);
  return startTime ? `${day} · ${startTime}` : day;
}

/** Compact recurrence for lists: "Semanal · Seg, Qua", "Diário", "Mensal"; null for a single date. */
export function eventRecurrenceShortLabel(
  event: Pick<PartnerEvent, 'recurrenceFreq' | 'recurrenceWeekdays'>,
): string | null {
  if (!event.recurrenceFreq) return null;
  if (event.recurrenceFreq === 'daily')
    return translateFile('domain.events.utils', 'recurrence.daily');
  if (event.recurrenceFreq === 'monthly') {
    return translateFile('domain.events.utils', 'recurrence.monthly');
  }

  const weekly = translateFile('domain.events.utils', 'recurrence.weekly');
  if (!event.recurrenceWeekdays.length) return weekly;
  const days = [...event.recurrenceWeekdays].sort((a, b) => a - b).map(weekdayShortLabel);
  return `${weekly} · ${days.join(', ')}`;
}

/** "João Pessoa - PB". */
export function eventCityLabel(
  address?: Pick<PartnerEventAddress, 'city' | 'state'>,
): string | null {
  if (!address) return null;
  return [address.city, address.state].filter(Boolean).join(' - ') || null;
}

/** Event types offered in the form (P09). Stored as the label: `eventType` is free text in the API. */
const eventTypeLabel = translatedMap('domain.events.utils', {
  running: 'eventType.running',
  cycling: 'eventType.cycling',
  swimming: 'eventType.swimming',
  triathlon: 'eventType.triathlon',
  groupTraining: 'eventType.groupTraining',
  // `other` cannot be a catalogue key: it is the plural form (src/i18n/ARCHITECTURE.md).
  notListed: 'eventType.notListed',
} as const);

/** The options, plus the event's current type when it is not one of them (seeded or older events). */
export const getEventTypeOptions = (current?: string): string[] => {
  const options = Object.values(eventTypeLabel);
  return current && !options.includes(current) ? [current, ...options] : options;
};

export const RECURRENCE_FREQUENCIES = ['daily', 'weekly', 'monthly'] as const;

/** Sunday first, as the API counts weekdays (0 to 6). */
export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

export const weekdayShortLabel = (weekday: number) => shortWeekday(new Date(2024, 8, 1 + weekday));

export const emptyEventFormValues = (): EventFormValues => ({
  bannerUrl: '',
  name: '',
  eventType: '',
  description: '',
  startDate: '',
  startTime: '',
  recurrenceFreq: '',
  recurrenceWeekdays: [],
  recurrenceUntil: '',
  address: {
    postalCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
  },
  externalUrl: '',
});

export const fromPartnerEvent = (event: PartnerEvent): EventFormValues => {
  const address = event.addresses[0];
  return {
    bannerUrl: event.bannerUrl ?? '',
    name: event.name,
    eventType: event.eventType ?? '',
    description: event.description ?? '',
    startDate: event.startDate.slice(0, 10),
    startTime: event.startTime ?? '',
    recurrenceFreq: event.recurrenceFreq ?? '',
    recurrenceWeekdays: event.recurrenceWeekdays,
    recurrenceUntil: event.recurrenceUntil?.slice(0, 10) ?? '',
    address: {
      postalCode: address?.postalCode ?? '',
      street: address?.street ?? '',
      number: address?.number ?? '',
      complement: address?.complement ?? '',
      neighborhood: address?.neighborhood ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
    },
    externalUrl: event.externalUrl ?? '',
  };
};

export const toPartnerEventBody = (values: EventFormValues): PartnerEventBody => {
  const textOrNull = (value: string) => value.trim() || null;
  const { address } = values;
  return {
    name: values.name.trim(),
    description: textOrNull(values.description),
    bannerUrl: values.bannerUrl || null,
    eventType: textOrNull(values.eventType),
    startDate: values.startDate,
    startTime: values.startTime || null,
    recurrenceFreq: values.recurrenceFreq || null,
    recurrenceWeekdays: values.recurrenceFreq === 'weekly' ? values.recurrenceWeekdays : [],
    recurrenceUntil: values.recurrenceFreq ? values.recurrenceUntil || null : null,
    externalUrl: textOrNull(values.externalUrl),
    address: {
      street: address.street.trim(),
      number: textOrNull(address.number),
      complement: textOrNull(address.complement),
      neighborhood: textOrNull(address.neighborhood),
      city: address.city.trim(),
      state: address.state,
      country: 'BR',
      postalCode: textOrNull(address.postalCode),
    },
  };
};

const EVENT_FORM_FIELDS = new Set<string>([
  ...Object.keys(emptyEventFormValues()),
  ...Object.keys(emptyEventFormValues().address).map((key) => `address.${key}`),
]);

/** API field errors on these paths land under their input; the rest go to the top alert. */
export const isEventFormVisibleField = (path: string) => EVENT_FORM_FIELDS.has(path);

export const getEventFormSaveMode = (
  event?: Pick<PartnerEvent, 'reviewStatus'> | null,
): EventFormSaveMode => {
  if (event?.reviewStatus === 'in_review') return 'in_review';
  if (event?.reviewStatus === 'approved') return 'approved';
  return 'draft';
};

/** "Salvar e enviar" right from the form: a draft (new, draft or rejected) of an approved account. */
export const canSubmitFromForm = (mode: EventFormSaveMode, partnerStatus: PartnerStatus) =>
  mode === 'draft' && partnerStatus === 'approved';

/** Today as YYYY-MM-DD in local time, the earliest start date for a new event. */
export const todayDateString = (now = new Date()) =>
  [now.getFullYear(), now.getMonth() + 1, now.getDate()]
    .map((part) => String(part).padStart(2, '0'))
    .join('-');

export const EVENT_SUBMIT_REQUIREMENTS: EventSubmitRequirement[] = [
  'accountApproved',
  'addressLocated',
  'upcomingDate',
];

/** Each P10 checklist item, met or not. The API answers 409/422 for the same rules. */
export const getEventSubmitChecklist = (
  event: Pick<PartnerEvent, 'locationResolved' | 'nextOccurrence'>,
  partnerStatus: PartnerStatus,
): Record<EventSubmitRequirement, boolean> => ({
  accountApproved: partnerStatus === 'approved',
  addressLocated: event.locationResolved,
  upcomingDate: event.nextOccurrence !== null,
});

/**
 * Actions on the detail page, mirroring the API lifecycle:
 * - edit: anything not canceled;
 * - submit: a draft or rejected event, once every checklist item is met;
 * - cancel: an event in review or published (a draft is deleted instead);
 * - delete: only without confirmations from today on (`upcomingIntentsCount`).
 */
export const getPartnerEventActions = (
  event: Pick<
    PartnerEventDetail,
    'reviewStatus' | 'canceledAt' | 'locationResolved' | 'nextOccurrence' | 'upcomingIntentsCount'
  >,
  partnerStatus: PartnerStatus,
): PartnerEventActions => {
  const canceled = Boolean(event.canceledAt);
  const submittable =
    !canceled && (event.reviewStatus === 'draft' || event.reviewStatus === 'rejected');
  const checklist = getEventSubmitChecklist(event, partnerStatus);
  const submitBlockedBy = submittable
    ? (EVENT_SUBMIT_REQUIREMENTS.find((requirement) => !checklist[requirement]) ?? null)
    : null;

  return {
    edit: !canceled,
    submit: submittable && submitBlockedBy === null,
    submitBlockedBy,
    cancel: !canceled && (event.reviewStatus === 'in_review' || event.reviewStatus === 'approved'),
    remove: event.upcomingIntentsCount === 0,
  };
};

/** "10 out, 14:20" for review history and status dates. */
export function formatEventDateTime(iso: string): string {
  return new Intl.DateTimeFormat(getActiveLocale(), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(new Date(iso))
    .replace('.', '')
    .replace(' de ', ' ');
}

/** "Av. Cabo Branco, 3000 · Cabo Branco · João Pessoa - PB". */
export function eventAddressLabel(
  address?: Pick<PartnerEventAddress, 'street' | 'number' | 'neighborhood' | 'city' | 'state'>,
): string | null {
  if (!address) return null;
  const line = [address.street, address.number].filter(Boolean).join(', ');
  return [line, address.neighborhood, eventCityLabel(address)].filter(Boolean).join(' · ') || null;
}

/** History line key for a review entry: who did what (P11–P13). The admin name never shows. */
export const getEventReviewHistoryKey = (
  review: Pick<PartnerEventReview, 'status' | 'decidedByBodyprint'>,
  isFirst: boolean,
) => {
  if (review.decidedByBodyprint) {
    return review.status === 'approved' ? 'approvedByBodyprint' : 'rejectedByBodyprint';
  }
  if (review.status === 'in_review') return 'submitted';
  return isFirst ? 'created' : 'backToDraft';
};
